import {gql} from "@apollo/client";

export const SEND_ENERGY_AUCTION_EMAILS_MUTATION = gql`
	mutation SendEmailsAsync($input: SendEmailsInput!) {
		sendEmails(input: $input) {
			success
			emailsSent
		}
	}
`;

export const ENERGY_AUCTION_EMAILS_QUERY = gql`
	query EnergyAuctionEmailsQuery(
		$first: Int
		$after: String
		$where: EnergyAuctionFilterInput
	) {
		energyAuctions(
			first: $first
			after: $after
			where: $where
		) {
			pageInfo {
				hasNextPage
				endCursor
			}
			nodes {
				email
			}
		}
	}
`;

export const ENERGY_AUCTION_REPORT_QUERY = gql`
	query EnergyAuctionQuery(
		$first: Int
		$after: String
		$where: EnergyAuctionFilterInput
		$order: [EnergyAuctionSortInput!]
	) {
		energyAuctions(
			first: $first
			after: $after
			where: $where
			order: $order
		) {
			pageInfo {
				hasNextPage
				hasPreviousPage
				startCursor
				endCursor
			}
			nodes {
				id
				created
				lastModified
				lastEmailSend
				email
				transactionReference
				genderOrLegalPersonType
				initials
				prefixes
				surname
				postalCode
				houseNumber
				houseNumberAddition
				city
				street
				tariff
				sustainable
				gasCapacity
				smartMeter
				customerType
				supplyType
				withSavings
				pricesExcludingTax
				hideUnavailableProducts
				productCategory
				currentProvider
				electricityUsageHigh
				electricityUsageLow
				electricityCapacity
				gasUsage
				electricitySupplyLow
				electricitySupplyHigh
				doubleMeter
				applyEnergyCeiling
				auctionProducts
				contractType
				previousContractEndDate
				hasSolarPanels
			}
		}
	}
`;

export const SEND_COMPARATOR_INTAKES_EMAILS_MUTATION = gql`
	mutation SendEmails($input: SendEmailsInput!) {
		sendEmails(input: $input) {
			success
			emailsQueued
			emailsExcluded
		}
	}
`;

// Only the ids, so the "Send Emails" button can page every row matching the grid's filter without
// pulling whole records. The send is addressed by id rather than by email because one address can hold
// an intake per product type per campaign, and only the filtered ones should go out - which is how a
// per-campaign send is done: filter the campaignName column, then send.
export const COMPARATOR_INTAKES_IDS_QUERY = gql`
	query ComparatorIntakeIdsQuery(
		$first: Int
		$after: String
		$where: ComparatorIntakeFilterInput
	) {
		comparatorIntakes(
			first: $first
			after: $after
			where: $where
		) {
			pageInfo {
				hasNextPage
				endCursor
			}
			nodes {
				id
			}
		}
	}
`;

export const COMPARATOR_INTAKES_REPORT_QUERY = gql`
	query ComparatorIntakesQuery(
		$first: Int
		$after: String
		$where: ComparatorIntakeFilterInput
		$order: [ComparatorIntakeSortInput!]
	) {
		comparatorIntakes(
			first: $first
			after: $after
			where: $where
			order: $order
		) {
			pageInfo {
				hasNextPage
				hasPreviousPage
				startCursor
				endCursor
			}
			nodes {
				id
				comparisonId
				email
				productType
				campaignName
				clientName
				created
				lastModified
				lastEmailSend
			}
		}
	}
`;

export const PRODUCT_COMMISSIONS_REPORT_PRODUCTS = gql`
query Query(
		$stage: String
		$first: Int
		$after: String
		$where: ProductFilterInput
		$order: [ProductSortInput!]
	) {
		products(
			stage: $stage
			first: $first
			after: $after
			where: $where
			order: $order
		) {
			pageInfo {
				hasNextPage
				hasPreviousPage
				startCursor
				endCursor
			}
			nodes {
				krn
				externalId
				dataProviderKrn
				name
				description
				productSupplierKrn
				state
				productType
				productTypeKrn
				orderType
				productCategory
				affiliateUrl
				insuranceCardUrl
				termsAndConditionsUrl
				clientName
				supplierGenericProduct
				creationDateTime
				lastModifiedDateTime
				lastModifiedBy
			}
		}
	}
`;
export const PRODUCT_COMMISSIONS_REPORT_SETUP = gql`
query Query($stage: String,$krns:[String!],$pageSize:Int!)  {
    productCommissions(
    	stage: $stage
        first: $pageSize
        where:{
            krn:
            {
                in:$krns
            }
        }
    )
    {
        pageInfo
        {
            endCursor
            hasNextPage
        }
        nodes
        {
            id
            krn
            commissionConfigs {
                startDate
                numberOfMonths
                commissionNetPremiumFactorPerBillablePerson
                commissionNetPremiumFactor
                commissionAmountPerMonth
                commissionAmountPerBillablePerson
                commissionAmountFixedFee
                renewalCommissionInstallmentAmount
				renewalCommissionInstallmentNumberOfMonths
				renewalCommissionInstallmentAmountLink
            }
            commissionShareConfigs {
                startDate
                partnerFixedAmount
                partnerCommissionFactor
            }
        }
    }
}
`;

export const GET_INCENTIVE_REGISTER = gql`
query Query($startDate:DateTime!,$endDate:DateTime!,$incentiveName:String!,$cursorAfter:String,$pageSize:Int!)
{
    incentiveRegister
    (
        after: $cursorAfter
        first: $pageSize
        where:{
            incentiveName:{eq:$incentiveName}
            transactionDate:{gte:$startDate }
            and:{
                transactionDate:{lte:$endDate }
            }
        }
        order: { transactionDate: ASC}
    )
    {
        pageInfo
        { endCursor hasNextPage}
        nodes
        {
            incentiveName
            creationDateTime
            email
            prefix
            firstName
            surname
            birthDate
            houseNumber
            houseNumberAddition
            postcode
            transactionDate
        }
    }
}
`;

export const GET_INCENTIVE_REGISTER_NAME_OPTIONS = gql`
query GetDistinctIncentiveNames
{
    distinctIncentiveNames
    {
       options
    }
}
`;

export const ORDERS_CUSTOM = gql`
query GetOrderDetail(		
	$first: Int
	$after: String) {
	orderDetailsExtended(
		first: $first
		after: $after,
		where: {
			productCategory: {eq: HEALTH_CARE}
			apiClientName: {eq: "consumentenbond"}
			orderType: {eq: FUNNEL_MGA}
			mainApplicant: {
				customProperties: {
					consumentenbondNewsletter: {eq: true}
				}
			},
			creationDateTime: {gt: "2024-10-01"}
		}
	)
	{
		pageInfo {
			hasNextPage
			hasPreviousPage
			startCursor
			endCursor
		}
		nodes {
			id
			supplierName
			surname
			email
			policyId
			apiClientName
			status
			orderType
			productCategory
			creationDateTime
			mainApplicant {
				mobile
				email
				customProperties {
					consumentenbondMember
					consumentenbondUserId,
					consumentenbondNewsletter
				}
			}
		}
	}
}`;
